import React from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Button
} from '@mui/material';
import {
  HelpOutline as HelpIcon,
  QuestionAnswer as FAQIcon,
  ConfirmationNumber as TicketIcon,
  MenuBook as KnowledgeIcon,
  VideoLibrary as VideoIcon,
  Email as EmailIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface SupportCard {
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  color: string;
}

export const SupportPage: React.FC = () => {
  const navigate = useNavigate();

  const supportCards: SupportCard[] = [
    {
      title: 'FAQs',
      description: 'Find quick answers to frequently asked questions',
      icon: <FAQIcon sx={{ fontSize: 40 }} />,
      path: '/support/faqs',
      color: '#14B8A6'
    },
    {
      title: 'Support Tickets',
      description: 'Create and track support tickets',
      icon: <TicketIcon sx={{ fontSize: 40 }} />,
      path: '/support/tickets',
      color: '#F59E0B'
    },
    {
      title: 'Knowledge Base',
      description: 'Browse detailed articles and guides',
      icon: <KnowledgeIcon sx={{ fontSize: 40 }} />,
      path: '/support/knowledge-base',
      color: '#8B5CF6'
    },
    {
      title: 'Video Tutorials',
      description: 'Watch step-by-step video guides',
      icon: <VideoIcon sx={{ fontSize: 40 }} />,
      path: '/support/videos',
      color: '#EF4444'
    },
    {
      title: 'Help Documentation',
      description: 'Complete documentation and guides',
      icon: <HelpIcon sx={{ fontSize: 40 }} />,
      path: '/support/help',
      color: '#10B981'
    },
    {
      title: 'Contact Us',
      description: 'Get in touch with our support team',
      icon: <EmailIcon sx={{ fontSize: 40 }} />,
      path: '/support/contact',
      color: '#3B82F6'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, fontSize: { xs: '1.75rem', md: '2rem' } }}>
          Support & Help Center
        </Typography>
        <Typography variant="body1" sx={{ color: '#666' }}>
          How can we help you today?
        </Typography>
      </Box>

      {/* Support Cards */}
      <Grid container spacing={3}>
        {supportCards.map((card) => (
          <Grid item xs={12} sm={6} md={4} key={card.path}>
            <Card
              sx={{
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}
              onClick={() => navigate(card.path)}
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: 2,
                    backgroundColor: `${card.color}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2
                  }}
                >
                  <Box sx={{ color: card.color }}>
                    {card.icon}
                  </Box>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {card.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                  {card.description}
                </Typography>
                <Button
                  endIcon={<ArrowForwardIcon />}
                  sx={{ color: card.color, p: 0 }}
                >
                  Explore
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Quick Stats */}
      <Box sx={{ mt: 6 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <Card sx={{ textAlign: 'center', p: 3, backgroundColor: '#f9f9f9' }}>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#14B8A6', mb: 1 }}>
                24/7
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Support Available
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ textAlign: 'center', p: 3, backgroundColor: '#f9f9f9' }}>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#F59E0B', mb: 1 }}>
                &lt;2h
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Average Response Time
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ textAlign: 'center', p: 3, backgroundColor: '#f9f9f9' }}>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#8B5CF6', mb: 1 }}>
                98%
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Customer Satisfaction
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default SupportPage;
